import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactReadMoreReadLess from "react-read-more-read-less";
import Select, { components } from 'react-select';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { KTSVG } from '../../../../_metronic/helpers';
import { PageTitle } from '../../../../_metronic/layout/core';
import DateComp from '../../../../components/dateComponent/DateComp';
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp';
import DateTimeComp from '../../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../../components/noRecord/NoRecordFound';
import Pagination from '../../../../components/pagination/Pagination';
import SearchBarComponent from '../../../../components/searchBar/SearchBarComponent';
import TableHeading from '../../../../components/tableHeading/TableHeading';
import TableSettingMenu from '../../../../components/tableHeading/TableSettingMenu';
import SearchSelect from '../../../../components/textFields/SearchSelect';
import TextField from '../../../../components/textFields/TextField';
import { constraintConfig } from '../../../../constraintConfig';
import { selectAuth } from '../../../../features/authSlice';
import { useAppSelector } from '../../../../hooks';
import {
  useGetAllUsersLookupQuery,
  useGetAppRequestSeverityLookupQuery,
  useGetSupportRequestStatusLookupQuery,
  useGetSupportUserLookupQuery,
} from '../../../../services/GeneralApi';
import { useGetAllPortalSupportRequestQuery } from '../../../../services/PortalSupportApi';
import useColumnVisibility from '../../../customHooks/useColumnVisibility';
import labelKey from '../../../localization/label.json';
import tosterKey from "../../../localization/toster.json";
import { topics } from './HelpCenterDropdowns';
import UpdateSupportRequestModal from './UpdateSupportRequestModal';
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent';

interface Option {
  value: string | number;
  label: string;
}

const DEFAULT_STATUS_IDS = [1, 2, 3] // New, InQue, Assigned

const { Option: ReactSelectOption } = components

const CheckboxOption = (props: any) => (
  <ReactSelectOption {...props}>
    <div className='d-flex align-items-center gap-2 ss-multi-check-option w-100 cursor-pointer'>
      <input
        type='checkbox'
        className='form-check-input ss-multi-check-option__input m-0 cursor-pointer'
        checked={!!props.isSelected}
        onChange={() => null}
        tabIndex={-1}
      />
      <label className='ss-multi-check-option__label mb-0 cursor-pointer' title={props.label}>
        {props.label}
      </label>
    </div>
  </ReactSelectOption>
)

const severityBadgeClass = (severity?: { id?: number; value?: string } | null) => {
  const label = String(severity?.value || '').toLowerCase()
  if (label.includes('critical') || label.includes('high') || severity?.id === 1) return 'badge-light-danger'
  if (label.includes('medium') || severity?.id === 2) return 'badge-light-warning'
  if (label.includes('low') || severity?.id === 3) return 'badge-light-success'
  return 'badge-light-primary'
}

const HelpSupportPage = () => {
  const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'helpSupport' });
  const { roleID } = useAppSelector(selectAuth)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10);
  const [searchStr, setSearchStr] = useState(''); // debounce search
  const [sortBy, setSortBy] = useState("createDT");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedColumn, setSortedColumn] = useState("createDT");
  const [immediateSearchStr, setImmediateSearchStr] = useState('');

  const defaultStatusOptions: Option[] = useMemo(
    () => [
      { value: 1, label: 'New' },
      { value: 2, label: 'InQue' },
      { value: 3, label: 'Assigned' },
    ],
    []
  )

  const [filterByStatusIds, setFilterByStatusIds] = useState<Option[]>(defaultStatusOptions);
  const [filterbyTopic, setFilterbyTopic] = useState<Option | null>(null);
  const [filterbySection, setFilterbySection] = useState<Option | null>(null);
  const [filterByAssignToId, setFilterByAssignToId] = useState<Option | null>(null);
  const [filterByRequesterId, setFilterByRequesterId] = useState<Option | null>(null);
  const [filterBySeverityId, setFilterBySeverityId] = useState<Option | null>(null);

  const [filterbyRequestDateStart, setFilterbyRequestDateStart] = useState('')
  const [filterbyRequestDateEnd, setFilterbyRequestDateEnd] = useState('')
  const [filterEstimateDateStart, setFilterEstimateDateStart] = useState('')
  const [filterEstimateDateEnd, setFilterEstimateDateEnd] = useState('')
  const [filterLastUpdateDateStart, setFilterLastUpdateDateStart] = useState('')
  const [filterLastUpdateDateEnd, setFilterLastUpdateDateEnd] = useState('')
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const [localFilterByStatusIds, setLocalFilterByStatusIds] = useState<Option[]>(defaultStatusOptions);
  const [localFilterbyTopic, setLocalFilterbyTopic] = useState<Option | null>(null);
  const [localFilterbySection, setLocalFilterbySection] = useState<Option | null>(null);
  const [localFilterByAssignToId, setLocalFilterByAssignToId] = useState<Option | null>(null);
  const [localFilterByRequesterId, setLocalFilterByRequesterId] = useState<Option | null>(null);
  const [localFilterBySeverityId, setLocalFilterBySeverityId] = useState<Option | null>(null);

  const [localFilterbyRequestDateStart, setLocalFilterbyRequestDateStart] = useState('')
  const [localFilterbyRequestDateEnd, setLocalFilterbyRequestDateEnd] = useState('')
  const [localFilterEstimateDateStart, setLocalFilterEstimateDateStart] = useState('')
  const [localFilterEstimateDateEnd, setLocalFilterEstimateDateEnd] = useState('')
  const [localFilterLastUpdateDateStart, setLocalFilterLastUpdateDateStart] = useState('')
  const [localFilterLastUpdateDateEnd, setLocalFilterLastUpdateDateEnd] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const { data: getSupportUserLookup } = useGetSupportUserLookupQuery('')
  const { data: getSupportStatusLookup } = useGetSupportRequestStatusLookupQuery('')
  const { data: getSeverityLookup } = useGetAppRequestSeverityLookupQuery('')
  const { data: getAllUsersLookup } = useGetAllUsersLookupQuery('')

  const statusOptions: Option[] = useMemo(
    () =>
      (getSupportStatusLookup?.result || []).map((option: any) => ({
        value: option?.id,
        label: option.value,
      })),
    [getSupportStatusLookup]
  )

  useEffect(() => {
    if (!statusOptions.length) return
    const mappedDefaults = statusOptions.filter((o) => DEFAULT_STATUS_IDS.includes(Number(o.value)))
    if (!mappedDefaults.length) return
    const syncSelection = (prev: Option[]) => {
      const isDefaultSet =
        prev.length === DEFAULT_STATUS_IDS.length &&
        prev.every((p) => DEFAULT_STATUS_IDS.includes(Number(p.value)))
      if (isDefaultSet) return mappedDefaults
      return prev.map((p) => statusOptions.find((s) => Number(s.value) === Number(p.value)) || p)
    }
    setFilterByStatusIds(syncSelection)
    setLocalFilterByStatusIds(syncSelection)
  }, [statusOptions])

  const { data, isLoading, refetch, isSuccess } = useGetAllPortalSupportRequestQuery({
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchStr: searchStr,
    sortBy: sortBy,
    sortOrder: sortOrder,
    filterByStatusIds: filterByStatusIds.map((s) => s.value).join(','),
    filterbyTopic: filterbyTopic ? filterbyTopic.value : '',
    filterbySection: filterbySection ? filterbySection.value : '',
    filterByAssignToId: filterByAssignToId ? filterByAssignToId.value : 0,
    filterByRequesterId: filterByRequesterId ? filterByRequesterId.value : 0,
    filterbyRequestDateStart: filterbyRequestDateStart || undefined,
    filterbyRequestDateEnd: filterbyRequestDateEnd || undefined,
    estimateDateStart: filterEstimateDateStart || undefined,
    estimateDateEnd: filterEstimateDateEnd || undefined,
    lastUpdateDateStart: filterLastUpdateDateStart || undefined,
    lastUpdateDateEnd: filterLastUpdateDateEnd || undefined,
    filterBySeverityId: filterBySeverityId ? filterBySeverityId.value : 0,
  })
  const [showUpdateSupportRequestModal, setShowUpdateSupportRequestModal] = useState(false)
  const [showUpdateSupportRequestEdit, setShowDivisionEdit] = useState({})
  const [viewOnlyData, setViewOnlyData] = useState(false)

  const divisionModalHandler = () => {
    setShowUpdateSupportRequestModal((prev) => !prev);
  };

  const handlePageSizeChange = (e: any) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setPageNumber(1); // Reset page number to 1
  };
  // pagination
  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1);
  };
  const handleSort = (property: string) => {
    setSortBy(property);
    if (sortedColumn === property) {
      setSortOrder(prevSortOrder => (prevSortOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(property);
      setSortOrder("asc");
    }
  };
  const handleSearchChange = (e: any) => {
    const { value } = e.target;
    setImmediateSearchStr(value); // Update immediate search state
    delayedSearch(value); // Update debounced search state
    setPageNumber(1)
  };

  const delayedSearch = useCallback(
    debounce((searchValue) => {
      setSearchStr(searchValue);
    }, 500),
    []
  );
  useEffect(() => {
    if (searchStr === "" ||
      sortOrder === "" ||
      sortBy === "") {
      refetch();
    }
  }, [searchStr, sortOrder, sortBy]);

  const handleFilterByStatusIds = (selected: readonly Option[] | null) => {
    setLocalFilterByStatusIds(selected ? [...selected] : []);
  };

  // Helper to build CustomMultiValue showing 1 chip + "+X more" popover trigger
  const createCustomMultiValue = (
    title: string,
    selectedList: Array<{ value: any; label: string }>,
    onRemove: (val: any) => void,
    onClear: () => void,
    popoverId: string
  ) => {
    return (props: any) => {
      const { index } = props
      if (index < 1) {
        return <components.MultiValue {...props} />
      }
      if (index === 1) {
        const remainingCount = selectedList.length - 1
        return (
          <OverlayTrigger
            trigger='click'
            placement='bottom'
            rootClose
            overlay={
              <Popover id={popoverId} className='ss-selected-users-popover'>
                <Popover.Header className='d-flex align-items-center justify-content-between fs-7 fw-bold py-2 px-3'>
                  <span>
                    {title} ({selectedList.length})
                  </span>
                  <span
                    className='text-danger fs-8 fw-semibold cursor-pointer'
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      onClear()
                    }}
                  >
                    Clear All
                  </span>
                </Popover.Header>
                <Popover.Body className='p-2'>
                  <div className='d-flex flex-column gap-1 ss-popover-users-list'>
                    {selectedList.map((opt) => (
                      <div
                        key={String(opt.value)}
                        className='d-flex align-items-center justify-content-between gap-2 px-2 py-1 rounded ss-popover-user-item'
                      >
                        <span className='fs-8 text-truncate' title={opt.label}>
                          {opt.label}
                        </span>
                        <span
                          className='cursor-pointer text-muted hover-danger fs-7 ss-popover-remove-btn'
                          title='Remove'
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            onRemove(opt.value)
                          }}
                        >
                          ✕
                        </span>
                      </div>
                    ))}
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <span
              className='badge badge-light-primary fw-bold cursor-pointer ss-show-more-badge ms-1'
              title={`Click to view all ${title.toLowerCase()}`}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            >
              +{remainingCount} more
            </span>
          </OverlayTrigger>
        )
      }
      return null
    }
  }

  const StatusMultiValue = useMemo(
    () =>
      createCustomMultiValue(
        'Selected Statuses',
        localFilterByStatusIds,
        (val) => {
          const updated = localFilterByStatusIds.filter((item) => item.value !== val)
          handleFilterByStatusIds(updated)
        },
        () => handleFilterByStatusIds([]),
        'selected-status-popover-support'
      ),
    [localFilterByStatusIds]
  )
  const handleFilterbyTopic = (selectedOption: Option | null) => {
    setLocalFilterbyTopic(selectedOption);
  };
  const handleFilterbySection = (selectedOption: Option | null) => {
    setLocalFilterbySection(selectedOption);
  };
  const handleFilterByAssignToId = (selectedOption: Option | null) => {
    setLocalFilterByAssignToId(selectedOption);
  };
  const handleFilterByRequesterId = (selectedOption: Option | null) => {
    setLocalFilterByRequesterId(selectedOption);
  };
  const handleFilterBySeverityId = (selectedOption: Option | null) => {
    setLocalFilterBySeverityId(selectedOption);
  };

  const handleRequestDateRange = (_e: any, picker: any) => {
    setLocalFilterbyRequestDateStart(picker.startDate.format('YYYY-MM-DD'))
    setLocalFilterbyRequestDateEnd(picker.endDate.format('YYYY-MM-DD'))
  }
  const handleClearRequestDateRange = () => {
    setLocalFilterbyRequestDateStart('')
    setLocalFilterbyRequestDateEnd('')
  }
  const handleEstimateDateRange = (_e: any, picker: any) => {
    setLocalFilterEstimateDateStart(picker.startDate.format('YYYY-MM-DD'))
    setLocalFilterEstimateDateEnd(picker.endDate.format('YYYY-MM-DD'))
  }
  const handleClearEstimateDateRange = () => {
    setLocalFilterEstimateDateStart('')
    setLocalFilterEstimateDateEnd('')
  }
  const handleLastUpdateDateRange = (_e: any, picker: any) => {
    setLocalFilterLastUpdateDateStart(picker.startDate.format('YYYY-MM-DD'))
    setLocalFilterLastUpdateDateEnd(picker.endDate.format('YYYY-MM-DD'))
  }
  const handleClearLastUpdateDateRange = () => {
    setLocalFilterLastUpdateDateStart('')
    setLocalFilterLastUpdateDateEnd('')
  }

  const debouncedRefetch = debounce(() => {
    refetch().finally(() => {
      setIsApplyingFilters(false);
    });
  }, 300);
  useEffect(() => {
    if (isApplyingFilters) {
      debouncedRefetch();
    }
  }, [isApplyingFilters, isSuccess]);


  const applyFilters = () => {
    setIsApplyingFilters(true);
    setFilterByStatusIds(localFilterByStatusIds);
    setFilterbyTopic(localFilterbyTopic);
    setFilterbySection(localFilterbySection);
    setFilterByAssignToId(localFilterByAssignToId);
    setFilterByRequesterId(localFilterByRequesterId);
    setFilterBySeverityId(localFilterBySeverityId)
    setFilterbyRequestDateStart(localFilterbyRequestDateStart);
    setFilterbyRequestDateEnd(localFilterbyRequestDateEnd);
    setFilterEstimateDateStart(localFilterEstimateDateStart);
    setFilterEstimateDateEnd(localFilterEstimateDateEnd);
    setFilterLastUpdateDateStart(localFilterLastUpdateDateStart);
    setFilterLastUpdateDateEnd(localFilterLastUpdateDateEnd);
    setPageNumber(1)
  };
  const resetFilters = () => {
    const defaults = statusOptions.filter((o) => DEFAULT_STATUS_IDS.includes(Number(o.value)))
    const resetStatuses = defaults.length ? defaults : defaultStatusOptions
    setFilterByStatusIds(resetStatuses);
    setFilterbyTopic(null);
    setFilterbySection(null);
    setFilterByAssignToId(null);
    setFilterByRequesterId(null);
    setFilterBySeverityId(null)
    setFilterbyRequestDateStart("")
    setFilterbyRequestDateEnd("")
    setFilterEstimateDateStart("")
    setFilterEstimateDateEnd("")
    setFilterLastUpdateDateStart("")
    setFilterLastUpdateDateEnd("")
    setLocalFilterByStatusIds(resetStatuses);
    setLocalFilterbyTopic(null);
    setLocalFilterbySection(null);
    setLocalFilterByAssignToId(null);
    setLocalFilterByRequesterId(null);
    setLocalFilterBySeverityId(null)
    setLocalFilterbyRequestDateStart("")
    setLocalFilterbyRequestDateEnd("")
    setLocalFilterEstimateDateStart("")
    setLocalFilterEstimateDateEnd("")
    setLocalFilterLastUpdateDateStart("")
    setLocalFilterLastUpdateDateEnd("")
    setPageNumber(1)
    setIsApplyingFilters(true)
  }
  const modules = [
    ...(roleID !== constraintConfig.roleID.role10 ? [
      {
        id: 'Dashboard',
        value: 'Dashboard'
      },
      {
        id: 'Expense Wizard',
        value: 'Expense Wizard'
      },
      {
        id: 'Expense Line Items',
        value: 'Expense Line Items'
      },
      {
        id: 'Expense Reports',
        value: 'Expense Reports'
      },
      {
        id: 'Receipt Vault',
        value: 'Receipt Vault'
      },
      {
        id: 'Cards',
        value: 'Cards'
      },
      {
        id: 'Report Analytics',
        value: 'Report Analytics'
      },
    ] : []),
    ...(roleID !== constraintConfig.roleID.role5 && roleID !== constraintConfig.roleID.role10 ? [
      {
        id: 'Approval Queue',
        value: 'Approval Queue'
      },
    ] : []),
    ...(roleID === constraintConfig.roleID.role5 ? [
      {
        id: 'My Budget History',
        value: 'My Budget History'
      },
    ] : []),

    ...(roleID === constraintConfig.roleID.role4 ? [
      {
        id: 'Team Management',
        value: 'Team Management'
      },
      {
        id: 'Budget Allocation',
        value: 'Budget Allocation'
      },
    ] : []),
    ...(roleID === constraintConfig.roleID.role2 ? [
      {
        id: 'Expense Users',
        value: 'Expense Users'
      },
      {
        id: 'Org Settings',
        value: 'Org Settings'
      },
      {
        id: 'Vendor',
        value: 'Vendor'
      },
      {
        id: 'Workflow',
        value: 'Workflow'
      },
      {
        id: 'Mileage',
        value: 'Mileage'
      },
      {
        id: 'User Flex Budget',
        value: 'User Flex Budget'
      },
      {
        id: 'Category Budget Allocation',
        value: 'Category Budget Allocation'
      },
      {
        id: 'Categories',
        value: 'Categories'
      },
      {
        id: 'Locations',
        value: 'Locations'
      },
      {
        id: 'Departments',
        value: 'Departments'
      },
      {
        id: 'Divisions',
        value: 'Divisions'
      },
      {
        id: 'Countries',
        value: 'Countries'
      },
      {
        id: 'Expense Violation',
        value: 'Expense Violation'
      },
      {
        id: 'Automation - Bank CC Feed',
        value: 'Automation - Bank CC Feed'
      }
    ] : []),
    ...(roleID === constraintConfig.roleID.role10 ? [
      {
        id: 'Expense Approval',
        value: 'Expense Approval'
      },

    ] : [])
  ];
  const handleRefresh = () => {
    refetch()
      .then(() => {
        if (isSuccess) {
          toast.success(tosterKey.dataRefreshedSuccesfully);
        }
      });
  };
  return (
    <>
      <UpdateSupportRequestModal show={showUpdateSupportRequestModal}
        handleClose={() => setShowUpdateSupportRequestModal(false)} data={showUpdateSupportRequestEdit} viewOnlyData={viewOnlyData} refetch={refetch} />
      <PageTitle breadcrumbs={[]}>{labelKey.portalSupportRequests}</PageTitle>

      <div className='card ss-portal-support-card mb-5'>
        <div className='card-header border-0 pt-4 pb-2 flex-wrap gap-2 align-items-center'>
          <div className='d-flex align-items-center flex-wrap gap-2 min-w-0 flex-grow-1'>
            <SearchBarComponent
              placeholder='User request'
              value={immediateSearchStr}
              onChange={handleSearchChange}
              className='ss-user-toolbar-search-input'
              mainClassName='ss-table-search--sm my-0'
            />
            <Tooltip id="refreshData" place="top" />
            <RefreshComponent onClick={handleRefresh} />
            {!showFilters ? (
              <button
                type="button"
                className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap"
                onClick={() => setShowFilters(true)}
              >
                <i className="fa fa-filter me-1 text-white" />
                Show Filters
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap"
                onClick={() => setShowFilters(false)}
              >
                <i className="fa fa-eye-slash me-1 text-white" />
                Hide Filters
              </button>
            )}
          </div>
        </div>
        <div className='card-body py-3 ss-listing-card-body'>
          {showFilters && (
            <div className='ss-portal-support-filters mb-4'>
              <div className='row g-2 pt-1 ss-listing-filters align-items-end ss-report-filter-row'>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <DateRangeComp
                    label='Request Date Range'
                    startDate={localFilterbyRequestDateStart}
                    endDate={localFilterbyRequestDateEnd}
                    onApply={handleRequestDateRange}
                    onCancel={handleClearRequestDateRange}
                    className='ss-filter-control'
                    value={
                      localFilterbyRequestDateStart && localFilterbyRequestDateEnd
                        ? `${moment(localFilterbyRequestDateStart).format('MM/DD/YYYY')} - ${moment(localFilterbyRequestDateEnd).format('MM/DD/YYYY')}`
                        : ''
                    }
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <DateRangeComp
                    label='Estimated Date Range'
                    startDate={localFilterEstimateDateStart}
                    endDate={localFilterEstimateDateEnd}
                    onApply={handleEstimateDateRange}
                    onCancel={handleClearEstimateDateRange}
                    className='ss-filter-control'
                    value={
                      localFilterEstimateDateStart && localFilterEstimateDateEnd
                        ? `${moment(localFilterEstimateDateStart).format('MM/DD/YYYY')} - ${moment(localFilterEstimateDateEnd).format('MM/DD/YYYY')}`
                        : ''
                    }
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <DateRangeComp
                    label='Request Updated Date Range'
                    startDate={localFilterLastUpdateDateStart}
                    endDate={localFilterLastUpdateDateEnd}
                    onApply={handleLastUpdateDateRange}
                    onCancel={handleClearLastUpdateDateRange}
                    className='ss-filter-control'
                    value={
                      localFilterLastUpdateDateStart && localFilterLastUpdateDateEnd
                        ? `${moment(localFilterLastUpdateDateStart).format('MM/DD/YYYY')} - ${moment(localFilterLastUpdateDateEnd).format('MM/DD/YYYY')}`
                        : ''
                    }
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-3 ss-filter-col-status'>
                  <label className='form-label'>{labelKey.status}</label>
                  <Select
                    isMulti
                    options={statusOptions}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: CheckboxOption, MultiValue: StatusMultiValue }}
                    onChange={handleFilterByStatusIds}
                    value={localFilterByStatusIds}
                    placeholder='Select Status'
                    classNamePrefix='react-select'
                    className='filter-valueContainer ss-multi-status-select ss-users-multi-select ss-filter-control'
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <SearchSelect
                    label={labelKey.topic}
                    options={[
                      { value: '', label: 'Select Topic' },
                      ...(topics || []).map((option: any) => ({
                        value: option?.id,
                        label: option.value,
                        key: option.id,
                      }))]}
                    value={localFilterbyTopic}
                    placeholder="Select Topic"
                    selectClass='filter-valueContainer'
                    onChange={handleFilterbyTopic}
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <SearchSelect
                    label={labelKey.section}
                    options={[
                      { value: '', label: 'Select Section' },
                      ...(modules || []).map((option: any) => ({
                        value: option?.id,
                        label: option.value,
                        key: option.id,
                      }))]}
                    placeholder="Select Section"
                    selectClass='filter-valueContainer'
                    value={localFilterbySection}
                    onChange={handleFilterbySection}
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <SearchSelect
                    label={labelKey.requester}
                    options={[
                      ...(getAllUsersLookup?.result || []).map((option: any) => ({
                        value: option?.id,
                        label: option.value,
                        key: option.id,
                      }))]}
                    placeholder="Select Requester"
                    selectClass='filter-valueContainer'
                    value={localFilterByRequesterId}
                    onChange={handleFilterByRequesterId}
                    isClearable
                  />
                </div>
                {roleID !== constraintConfig.roleID.roleIDPortalSupport && (
                  <div className='col-md-4 col-lg-3 col-xl-2'>
                    <SearchSelect
                      label={labelKey.assignedToUser}
                      options={[
                        ...(getSupportUserLookup?.result || []).map((option: any) => ({
                          value: option?.id,
                          label: option.value,
                          key: option.id,
                        }))]}
                      placeholder="Select User"
                      selectClass='filter-valueContainer'
                      value={localFilterByAssignToId}
                      onChange={handleFilterByAssignToId}
                      isClearable
                    />
                  </div>
                )}
                <div className='col-md-4 col-lg-3 col-xl-2'>
                  <SearchSelect
                    label={labelKey.severity}
                    options={[
                      ...(getSeverityLookup?.result || []).map((option: any) => ({
                        value: option?.id,
                        label: option.value,
                        key: option.id,
                      }))]}
                    placeholder="Select Severity"
                    selectClass='filter-valueContainer'
                    value={localFilterBySeverityId}
                    onChange={handleFilterBySeverityId}
                    isClearable
                  />
                </div>
                <div className='col-md-4 col-lg-3 col-xl-2 ss-listing-filter-actions-col'>
                  <div className='d-flex gap-2 ss-listing-filter-actions'>
                    <button type='button' className='btn btn-sm btn-danger ss-smart-btn text-nowrap' onClick={resetFilters}>
                      Reset Filters
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary ss-smart-btn text-nowrap'
                      onClick={applyFilters}
                      disabled={isApplyingFilters}
                    >
                      {isApplyingFilters ? (
                        <>
                          <span>loading...</span>
                          <span className='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true' />
                        </>
                      ) : (
                        'Apply Filters'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Tooltip id="update-SupportRequest" place="left" positionStrategy="fixed" />
          <Tooltip id="detail" place="left" positionStrategy="fixed" />
          <div className='table-responsive table-height'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 ss-portal-support-table'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    {hiddenColumns.includes('requestTrackingID') && (
                      <TableHeading
                        label={labelKey.requestTrackingID}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='requestTrackingID'
                        className='ps-4'
                      />
                    )}
                    {hiddenColumns.includes('appRequestSeverity') && (
                      <TableHeading
                        label={labelKey.appRequestSeverity}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='appRequestSeverity'
                        className='text-center'
                        labelClassName='justify-content-center'
                      />
                    )}
                    {hiddenColumns.includes('status') && (
                      <TableHeading
                        label={labelKey.status}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='status'
                        className='text-center'
                        labelClassName='justify-content-center'
                      />
                    )}
                    {hiddenColumns.includes('sr') && (
                      <TableHeading
                        label={labelKey.requestDate}
                        columnId='sr'
                      />
                    )}
                    {hiddenColumns.includes('requester') && (
                      <TableHeading
                        label={labelKey.requester}
                        columnId='requester'
                      />
                    )}
                    {hiddenColumns.includes('topic') && (
                      <TableHeading
                        label={labelKey.topic}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='topic'
                      />
                    )}
                    {hiddenColumns.includes('section') && (
                      <TableHeading
                        label={labelKey.section}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='section'
                      />
                    )}
                    {hiddenColumns.includes('subject') && (
                      <TableHeading
                        label={labelKey.subject}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='subject'
                        labelClassName='w-200px'
                      />
                    )}
                    {hiddenColumns.includes('description') && (
                      <TableHeading
                        label={labelKey.description}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='description'
                        labelClassName='w-300px'
                      />
                    )}
                    {hiddenColumns.includes('assignedTo') && (
                      <TableHeading
                        label={labelKey.assignedTo}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='assignedTo'
                      />
                    )}
                    {hiddenColumns.includes('assignedToUserDate') && (
                      <TableHeading
                        label={labelKey.assignedToUserDate}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='assignedToUserDate'
                      />
                    )}
                    {hiddenColumns.includes('appResolutionType') && (
                      <TableHeading
                        label={labelKey.appResolutionType}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='appResolutionType'
                      />
                    )}
                    {hiddenColumns.includes('qaCompleted') && (
                      <TableHeading
                        label={labelKey.qaCompleted}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='qaCompleted'
                      />
                    )}
                    {hiddenColumns.includes('deploymentNeeded') && (
                      <TableHeading
                        label={labelKey.deploymentNeeded}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='deploymentNeeded'
                      />
                    )}
                    {hiddenColumns.includes('deploymentReady') && (
                      <TableHeading
                        label={labelKey.deploymentReady}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='deploymentReady'
                      />
                    )}
                    {hiddenColumns.includes('deployed') && (
                      <TableHeading
                        label={labelKey.deployed}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='deployed'
                      />
                    )}
                    {hiddenColumns.includes('estimatedDate') && (
                      <TableHeading
                        label={labelKey.estimatedDate}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='estimatedDate'
                        className='text-center'
                        labelClassName='justify-content-center'
                      />
                    )}
                    {hiddenColumns.includes('lastUpdateDate') && (
                      <TableHeading
                        label={labelKey.lastUpdateDate}
                        sortedColumn={sortedColumn}
                        sortOrder={sortOrder}
                        handleSort={handleSort}
                        columnId='lastUpdateDate'
                        className='text-center'
                        labelClassName='justify-content-center'
                      />
                    )}
                    <th className='text-end rounded-end pe-2 border-0 sticky-table-actions'>
                      <div className='d-flex align-items-center justify-content-end gap-2'>
                        {hiddenColumns.includes('action') && (
                          <span>{labelKey.actions}</span>
                        )}
                        <TableSettingMenu>
                        <TextField
                          rightLabel={labelKey.requestTrackingID}
                          type="checkbox"
                          onChange={() => handleToggleColumn("requestTrackingID")}
                          checked={hiddenColumns.includes('requestTrackingID')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.appRequestSeverity}
                          type="checkbox"
                          onChange={() => handleToggleColumn("appRequestSeverity")}
                          checked={hiddenColumns.includes('appRequestSeverity')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.status}
                          type="checkbox"
                          onChange={() => handleToggleColumn("status")}
                          checked={hiddenColumns.includes('status')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.requestDate}
                          type="checkbox"
                          onChange={() => handleToggleColumn("sr")}
                          checked={hiddenColumns.includes('sr')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.requester}
                          type="checkbox"
                          onChange={() => handleToggleColumn("requester")}
                          checked={hiddenColumns.includes('requester')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.topic}
                          type="checkbox"
                          onChange={() => handleToggleColumn("topic")}
                          checked={hiddenColumns.includes('topic')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.section}
                          type="checkbox"
                          onChange={() => handleToggleColumn("section")}
                          checked={hiddenColumns.includes('section')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.subject}
                          type="checkbox"
                          onChange={() => handleToggleColumn("subject")}
                          checked={hiddenColumns.includes('subject')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.description}
                          type="checkbox"
                          onChange={() => handleToggleColumn("description")}
                          checked={hiddenColumns.includes('description')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.assignedTo}
                          type="checkbox"
                          onChange={() => handleToggleColumn("assignedTo")}
                          checked={hiddenColumns.includes('assignedTo')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.appResolutionType}
                          type="checkbox"
                          onChange={() => handleToggleColumn("appResolutionType")}
                          checked={hiddenColumns.includes('appResolutionType')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.qaCompleted}
                          type="checkbox"
                          onChange={() => handleToggleColumn("qaCompleted")}
                          checked={hiddenColumns.includes('qaCompleted')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.deploymentNeeded}
                          type="checkbox"
                          onChange={() => handleToggleColumn("deploymentNeeded")}
                          checked={hiddenColumns.includes('deploymentNeeded')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.deployed}
                          type="checkbox"
                          onChange={() => handleToggleColumn("deployed")}
                          checked={hiddenColumns.includes('deployed')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.deploymentReady}
                          type="checkbox"
                          onChange={() => handleToggleColumn("deploymentReady")}
                          checked={hiddenColumns.includes('deploymentReady')}
                          fieldClass='mb-4'
                        />

                        <TextField
                          rightLabel={labelKey.estimatedDate}
                          type="checkbox"
                          onChange={() => handleToggleColumn("estimatedDate")}
                          checked={hiddenColumns.includes('estimatedDate')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.lastUpdateDate}
                          type="checkbox"
                          onChange={() => handleToggleColumn("lastUpdateDate")}
                          checked={hiddenColumns.includes('lastUpdateDate')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.action}
                          type="checkbox"
                          onChange={() => handleToggleColumn("action")}
                          checked={hiddenColumns.includes('action')}
                          fieldClass='mb-4'
                        />
                      </TableSettingMenu>
                      </div>
                    </th>

                  </tr>
                </thead>
                <tbody>
                  {!isLoading &&
                    <>
                      {data?.result?.data?.length > 0 ? (
                        <>
                          {data?.result?.data?.map((data: any, index: any) => (
                            <tr key={index}>
                              {hiddenColumns.includes("requestTrackingID") && (
                                <td className='ps-4'>{data?.requestTrackingID}</td>
                              )}
                              {hiddenColumns.includes("appRequestSeverity") && (
                                <td className='text-center'>
                                  {data?.appRequestSeverity?.value ? (
                                    <span className={`badge ${severityBadgeClass(data.appRequestSeverity)} fs-7 fw-semibold`}>
                                      {data.appRequestSeverity.value}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              )}
                              {hiddenColumns.includes("status") && (
                                <td className='text-center'>{data?.supportRequestStatus?.value}</td>
                              )}
                              {hiddenColumns.includes("sr") && (
                                <td>
                                  <DateTimeComp formattedDate={data?.createdAt} />
                                </td>
                              )}
                              {hiddenColumns.includes("requester") && (
                                <td>{data?.requester?.value}</td>
                              )}
                              {hiddenColumns.includes("topic") && (
                                <td>{data?.topic}</td>
                              )}
                              {hiddenColumns.includes("section") && (
                                <td>{data?.section}</td>
                              )}
                              {hiddenColumns.includes("subject") && (
                                <td className='w-200px text-wrap'>
                                  {data && data.subject && <ReactReadMoreReadLess
                                    charLimit={50}
                                    readMoreText={"Read more"}
                                    readLessText={"Read less"}
                                    readMoreClassName="readMore"
                                    readLessClassName="readLess"
                                  >
                                    {data.subject}
                                  </ReactReadMoreReadLess>}
                                </td>
                              )}
                              {hiddenColumns.includes("description") && (
                                <td className='w-300px text-wrap'>
                                  {data && data.description && <ReactReadMoreReadLess
                                    charLimit={50}
                                    readMoreText={"Read more"}
                                    readLessText={"Read less"}
                                    readMoreClassName="readMore"
                                    readLessClassName="readLess"
                                  >
                                    {data.description}
                                  </ReactReadMoreReadLess>}</td>
                              )}
                              {hiddenColumns.includes("assignedTo") && (
                                <td>{data?.assignedToOrgUser?.value}</td>
                              )}
                              {hiddenColumns.includes("assignedToUserDate") && (
                                <td>{data?.assignedToUserDate}</td>
                              )}
                              {hiddenColumns.includes("appResolutionType") && (
                                <td>{data?.appResolutionType?.value}</td>
                              )}
                              {hiddenColumns.includes("qaCompleted") && (
                                <td>
                                  <TextField
                                    type='checkbox'
                                    checked={data?.qaCompleted}
                                    fieldClass='opacity-none ms-13'
                                    disabled={true} />
                                </td>
                              )}
                              {hiddenColumns.includes("deploymentNeeded") && (
                                <td>
                                  <TextField
                                    type='checkbox'
                                    checked={data?.deploymentNeeded}
                                    fieldClass='opacity-none ms-20'
                                    disabled={true} />
                                </td>
                              )}
                              {hiddenColumns.includes("deploymentReady") && (
                                <td>
                                  <TextField
                                    type='checkbox'
                                    checked={data?.deploymentReady}
                                    fieldClass='opacity-none ms-20'
                                    disabled={true}
                                  />
                                </td>
                              )}
                              {hiddenColumns.includes("deployed") && (
                                <td>
                                  <TextField
                                    type='checkbox'
                                    checked={data?.deployed}
                                    fieldClass='opacity-none ms-8'
                                    disabled={true} />
                                </td>
                              )}
                              {hiddenColumns.includes("estimatedDate") && (
                                <td className='text-center'><DateTimeComp formattedDate={data?.estimatedResolvedTime} /></td>
                              )}
                              {hiddenColumns.includes("lastUpdateDate") && (
                                <td className='text-center'><DateComp formattedDate={data?.lastUpdated} /></td>
                              )}
                              <td className='text-end sticky-table-actions'>
                                {hiddenColumns.includes('action') && (
                                  <div className='d-flex align-items-center justify-content-end gap-2'>
                                    <button
                                      type='button'
                                      data-tooltip-id="update-SupportRequest"
                                      data-tooltip-content={labelKey.updateSupportRequest}
                                      aria-label={labelKey.updateSupportRequest}
                                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm ss-portal-support-action-btn'
                                      onClick={() => {
                                        setViewOnlyData(false)
                                        setShowDivisionEdit(data);
                                        divisionModalHandler();
                                      }}
                                    >
                                      <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                                    </button>
                                    <button
                                      type='button'
                                      data-tooltip-id="detail"
                                      data-tooltip-content={labelKey.detail}
                                      aria-label={labelKey.detail}
                                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm ss-portal-support-action-btn'
                                      onClick={() => {
                                        setViewOnlyData(true)
                                        setShowDivisionEdit(data);
                                        divisionModalHandler();
                                      }}
                                    >
                                      <KTSVG
                                        path='/media/icons/duotune/arrows/arr064.svg'
                                        className='svg-icon-3'
                                      />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </>) : (
                        <tr>
                          <td colSpan={20}>
                            <NoRecordFound />
                          </td>
                        </tr>
                      )}
                    </>}
                  {isLoading && (
                    <tr>
                      <td colSpan={20} className="text-center">
                        <LoadingComponent />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
              toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
              ofResult={data?.result?.totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.result?.totalPages || 0}
              currentPage={pageNumber}
            />
        </div>
      </div>
    </>
  )
}

export default HelpSupportPage